import { Injectable } from '@angular/core';
import { filterNil } from '@datorama/akita';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { ComponentController } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
	distinctUntilChanged,
	map,
	pluck,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { BottomReachedTrigger } from 'presentation/common/spreeng-models';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

import { AppService } from '../../../app.service';
import {
	CoacheeProfileState,
	CoacheeProfileStore,
} from './coachee-profile.store';

@Injectable()
@UntilDestroy()
export class CoacheeProfileController extends ComponentController<CoacheeProfileState> {
	public i18n = 'presentation.page.profile.coachee-profile';

	public title$: Observable<string>;
	public coachee$: Observable<IUser>;
	public activeTabIndex$: Observable<number>;
	public bottomReached$: Observable<BottomReachedTrigger>;
	public meetingsChanged$: Observable<number>;

	constructor(
		store: CoacheeProfileStore,
		private _appService: AppService,
		private _routerQuery: RouterQuery,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public updateActiveTabIndex(index: number): void {
		this.store.update({ activeTabIndex: index });
	}

	public triggerBottomReached() {
		this.store.update({
			bottomReachedTrigger: {
				activeTabIndex: this.getValue().activeTabIndex,
				timestamp: Date.now(),
			},
		});
	}

	public triggerMeetingsReload() {
		this.store.update({ meetingChangedTrigger: Date.now() });
	}

	private _initListeners() {
		this._routerQuery
			.selectParams<string>('id')
			.pipe(
				filterNil,
				tap((id) => this.store.update({ coacheeId: id })),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.meetingsChanged$ = this.select('meetingChangedTrigger').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.bottomReached$ = this.select('bottomReachedTrigger').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.activeTabIndex$ = this.select('activeTabIndex').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coachee$ = this.select('coacheeId').pipe(
			filterNil,
			switchMap((coacheeId) =>
				this._userApplicationPort.observeUserById(coacheeId)
			),
			map((data) => data?.data),
			distinctUntilChanged((p, c) => p?.id === c?.id),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.title$ = this.coachee$.pipe(
			filterNil,
			pluck('name'),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}
}
